import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setAlertMessage, setAlertType } from '../../../redux/reducers/alertSlice';
import TagHelper from '../../../helpers/PostHelpers/TagHelper';
import { changeVideoPostStatus } from '../../../redux/network/coachAPI';
import { updateStandAloneVideoStatusFunction } from '../../../redux/network/standAloneApi';
import axios from 'axios';
import { GET_ALL_TAGS } from '../../../config/endpoints';
import {
  error_GetFetchVideoContent, isStarted_GetFetchVideoContent,
  success_GetFetchVideoContent,
  toggleVideoSortDropdown,
} from '../../../redux/reducers/videoSlice';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { getFetchGamePostFileThumbnail } from '../../../redux/network/videoApi';
import { ToastContainer } from 'react-toastify';
import GameVideoDetails from '../GameVideoDetails/GameVideoDetails';
import ReactGA from 'react-ga4';
import { ChevronRight, Home, Video } from 'lucide-react'; // Added Icons for breadcrumbs/polish

const safeParseJSON = (value) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getTagSignature = (list = []) => {
  if (!Array.isArray(list)) {
    return '';
  }
  return list
    .map((tag) => TagHelper.getTagIdentity(tag))
    .sort()
    .join('|');
};

// Register custom Video.js components (Keep existing component registrations)
if (!videojs.getComponent('SeekBackward')) {
  const Button = videojs.getComponent('Button');
  class SeekBackward extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Seek Backward 5s");
      this.addClass('vjs-seek-backward');
      this.el().innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
            <polygon points="17,4 9,10 17,16" fill="white" stroke="white"/>
            <polygon points="11,4 3,10 11,16" fill="white" stroke="white"/>
          </svg>
        `;
    }
    handleClick() {
      const video = this.player();
      video.currentTime(Math.max(0, video.currentTime() - 5));
    }
  }
  videojs.registerComponent('SeekBackward', SeekBackward);
}

if (!videojs.getComponent('SeekForward')) {
  const Button = videojs.getComponent('Button');
  class SeekForward extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Seek Forward 5s");
      this.addClass('vjs-seek-forward');
      this.el().innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
            <polygon points="3,4 11,10 3,16" fill="white" stroke="white"/>
            <polygon points="9,4 17,10 9,16" fill="white" stroke="white"/>
          </svg>
        `;
    }
    handleClick() {
      const video = this.player();
      video.currentTime(Math.min(video.duration(), video.currentTime() + 5));
    }
  }
  videojs.registerComponent('SeekForward', SeekForward);
}

if (!videojs.getComponent('ScreenshotButton')) {
  const Button = videojs.getComponent('Button');
  class ScreenshotButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Screenshot");
      this.addClass('vjs-screenshot');
      this.el().innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="8" width="12" height="8" rx="2" fill="white" stroke="white" stroke-width="2"/>
            <circle cx="10" cy="12" r="2" fill="white" stroke="white" stroke-width="2"/>
            <rect x="6" y="2" width="8" height="4" rx="1" fill="white" stroke="white" stroke-width="2"/>
          </svg>
        `;
    }
    handleClick() {
      const videoEl = this.player().el().querySelector('video');
      if (!videoEl) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = videoEl.videoWidth || 640;
        canvas.height = videoEl.videoHeight || 360;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
        const image = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = image;
        link.download = `screenshot-${Date.now()}.png`;
        link.click();
      } catch (err) {
        console.error("Screenshot failed:", err);
        // Using window.alert as fallback if toast is not available in this context easily
        alert("Failed to take screenshot. This might be due to security restrictions on the video source.");
      }
    }
  }
  videojs.registerComponent('ScreenshotButton', ScreenshotButton);
}

// Simple Video Player Component
const SimpleVideoPlayer = ({ videoUrl, thumbnailUrl, playerRef, videoRef, videoKey, setLoading }) => {
  const initializingRef = useRef(false);

  // Keep existing keyboard shortcuts logic
  const showKeyboardShortcuts = () => {
    const shortcuts = [
      'Spacebar: Play/Pause',
      '← → Arrow Keys: Seek 5 seconds',
      '↑ ↓ Arrow Keys: Volume up/down',
      'Page Up/Down: Seek 10 seconds',
      'Home/End: Seek to beginning/end',
      '0-9 Keys: Seek to percentage (10%-90%)',
      'Ctrl+F: Fullscreen toggle',
      'Ctrl+P: Picture-in-Picture',
      'Ctrl+S: Take screenshot',
      'F1 or Ctrl+H: Show this help'
    ];
    alert('🎮 Keyboard Shortcuts:\n\n' + shortcuts.join('\n'));
  };

  if (!videojs.getComponent('KeyboardHelpButton')) {
    const Button = videojs.getComponent('Button');
    class KeyboardHelpButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText("Keyboard Shortcuts");
        this.addClass('vjs-keyboard-help');
        this.el().innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" stroke-width="2" fill="none"/>
            <rect x="4" y="6" width="2" height="2" fill="white"/>
            <rect x="7" y="6" width="2" height="2" fill="white"/>
            <rect x="10" y="6" width="2" height="2" fill="white"/>
            <rect x="13" y="6" width="2" height="2" fill="white"/>
            <rect x="4" y="9" width="2" height="2" fill="white"/>
            <rect x="7" y="9" width="2" height="2" fill="white"/>
            <rect x="10" y="9" width="2" height="2" fill="white"/>
            <rect x="13" y="9" width="2" height="2" fill="white"/>
            <rect x="4" y="12" width="2" height="2" fill="white"/>
             <rect x="7" y="12" width="2" height="2" fill="white"/>
            <rect x="10" y="12" width="2" height="2" fill="white"/>
            <rect x="13" y="12" width="2" height="2" fill="white"/>
          </svg>
        `;
      }
      handleClick() { showKeyboardShortcuts(); }
    }
    videojs.registerComponent('KeyboardHelpButton', KeyboardHelpButton);
  }

  useEffect(() => {
    let handleKeyDown;
    let rafId;

    if (videoRef.current && !playerRef.current && videoUrl && !initializingRef.current) {
      try {
        rafId = window.requestAnimationFrame(() => {
          if (!videoRef.current || !videoRef.current.isConnected) return;
          initializingRef.current = true;
          const mimeType = "video/mp4";

          const player = videojs(videoRef.current, {
            controls: true,
            preload: "metadata",
            playbackRates: [0.1, 0.2, 0.5, 1, 2, 3, 5],
            poster: thumbnailUrl,
            sources: [{ src: videoUrl, type: mimeType }],
            crossorigin: 'anonymous',
            controlBar: {
              children: [
                'SeekBackward', 'playToggle', 'SeekForward', 'volumePanel',
                'playbackRateMenuButton', 'progressControl', 'ScreenshotButton',
                'KeyboardHelpButton', 'PictureInPictureToggle', 'fullscreenToggle',
              ]
            },
            userActions: { doubleClick: false, hotkeys: false },
            inactivityTimeout: 0,
            playsinline: true,
            fluid: true // Enable fluid mode for better responsiveness
          });

          playerRef.current = player;

          handleKeyDown = (e) => {
            // ... (Keep existing keyboard logic) ...
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            switch (e.key) {
              case 'ArrowRight': if (player) { e.preventDefault(); player.currentTime(Math.min(player.duration(), player.currentTime() + 5)); } break;
              case 'ArrowLeft': if (player) { e.preventDefault(); player.currentTime(Math.max(0, player.currentTime() - 5)); } break;
              case ' ': 
                if (e.key === ' ' || e.code === 'Space') {
                  e.preventDefault(); 
                  e.stopPropagation();
                  if (player) { player.paused() ? player.play() : player.pause(); } 
                }
                break;
              case 'f': case 'F': if ((e.ctrlKey || e.metaKey) && player) { e.preventDefault(); player.isFullscreen() ? player.exitFullscreen() : player.requestFullscreen(); } break;
              case 'p': case 'P': if ((e.ctrlKey || e.metaKey) && player) { e.preventDefault(); const videoEl = player.el().querySelector('video'); if (videoEl && document.pictureInPictureEnabled) { document.pictureInPictureElement ? document.exitPictureInPicture() : videoEl.requestPictureInPicture(); } } break;
              case 's': case 'S': if (e.ctrlKey && player) { 
                e.preventDefault(); 
                const videoEl = player.el().querySelector('video'); 
                if (videoEl) { 
                  try {
                    const canvas = document.createElement("canvas"); 
                    canvas.width = videoEl.videoWidth || 640; 
                    canvas.height = videoEl.videoHeight || 360; 
                    const ctx = canvas.getContext("2d"); 
                    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height); 
                    const image = canvas.toDataURL("image/png"); 
                    const link = document.createElement("a"); 
                    link.href = image; 
                    link.download = `screenshot-${Date.now()}.png`; 
                    link.click(); 
                  } catch (err) {
                    console.error("Keyboard screenshot failed:", err);
                    alert("Failed to take screenshot via keyboard.");
                  }
                } 
              } break;
              case 'ArrowUp': if (player) { e.preventDefault(); player.volume(Math.min(1, player.volume() + 0.1)); } break;
              case 'ArrowDown': if (player) { e.preventDefault(); player.volume(Math.max(0, player.volume() - 0.1)); } break;
              case '0': if (player) { e.preventDefault(); player.currentTime(0); } break;
              case '1': case '2': case '3': case '4': case '5': case '6': case '7': case '8': case '9': if (player) { e.preventDefault(); const percentage = parseInt(e.key) * 10; player.currentTime((player.duration() * percentage) / 100); } break;
              case 'Home': if (player) { e.preventDefault(); player.currentTime(0); } break;
              case 'End': if (player) { e.preventDefault(); player.currentTime(player.duration()); } break;
              case 'PageUp': if (player) { e.preventDefault(); player.currentTime(Math.min(player.duration(), player.currentTime() + 10)); } break;
              case 'PageDown': if (player) { e.preventDefault(); player.currentTime(Math.max(0, player.currentTime() - 10)); } break;
              case 'F1': case 'h': case 'H': if (e.key === 'F1' || (e.ctrlKey)) { e.preventDefault(); showKeyboardShortcuts(); } break;
              default: break;
            }
          };

          window.addEventListener('keydown', handleKeyDown);

          player.on("loadedmetadata", () => setLoading(false));
          player.on("canplay", () => setLoading(false));
          player.on("error", () => setLoading(false));

          return () => {
            if (rafId) window.cancelAnimationFrame(rafId);
            if (handleKeyDown) window.removeEventListener('keydown', handleKeyDown);
            try {
              if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
              }
            } catch (error) { }
            initializingRef.current = false;
          };
        });
      } catch (error) { setLoading(false); }
    }
  }, [videoUrl, thumbnailUrl, videoKey]);

  return (
    <div className="w-full h-full relative rounded-2xl overflow-hidden bg-black shadow-lg">
      <video
        key={videoKey}
        ref={videoRef}
        className="video-js vjs-big-play-centered"
        style={{ width: '100%', height: '100%' }}
        crossOrigin="anonymous"
        poster={thumbnailUrl}
      />
    </div>
  );
};

export default function Requestfeedback() {
  const env = process.env.REACT_APP_ENV;
  const jwtToken = typeof window !== 'undefined' ? localStorage.getItem('BQS_TOKEN') : null;
  const { postId: routePostId } = useParams();
  const [storedReviewPostId, setStoredReviewPostId] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('reviewPostId') : null));
  const reviewPostId = storedReviewPostId || routePostId || null;
  const [reviewPost, setReviewPost] = useState(() => safeParseJSON(typeof window !== 'undefined' && reviewPostId ? localStorage.getItem(`${reviewPostId}_reviewPost`) : null));

  // ... (Keep existing logic/effects mostly the same) ...
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (routePostId && routePostId !== storedReviewPostId) {
      localStorage.setItem('reviewPostId', routePostId);
      setStoredReviewPostId(routePostId);
    }
  }, [routePostId, storedReviewPostId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !reviewPostId) return;
    setReviewPost(safeParseJSON(localStorage.getItem(`${reviewPostId}_reviewPost`)));
  }, [reviewPostId]);

  const videoContent = useSelector((state) => state.video.videoContent);
  const navigate = useNavigate();
  const videoList = useSelector((state) => state.video.video?.data || []);
  const [tagDictionary, setTagDictionary] = useState({});

  const reduxVideo = useMemo(() => videoList.find((video) => video._id === reviewPostId), [videoList, reviewPostId]);
  const activeVideo = reviewPost || reduxVideo || null;
  const videoState = activeVideo?.state || 'created';
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const thumbnailUrl = videoThumbnails[reviewPostId];
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoKey, setVideoKey] = useState(Date.now());
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const rawVideoTags = location.state?.videoTags;

  // ... (Keep tag logic) ...
  const videoTagList = useMemo(() => {
    const directTags = Array.isArray(rawVideoTags) ? rawVideoTags : [];
    const cachedReviewTags = Array.isArray(reviewPost?.tags) ? reviewPost.tags : [];
    const reduxTags = Array.isArray(reduxVideo?.tags) ? reduxVideo.tags : [];
    const mergedSources = TagHelper.mergeTagLists(TagHelper.mergeTagLists(directTags, cachedReviewTags), reduxTags);
    if (!reviewPostId || typeof window === 'undefined') return mergedSources;
    const keyPath = reviewPost?.game?.[0]?.path || reduxVideo?.game?.[0]?.path;
    const persisted = TagHelper.getPersistedTags(reviewPostId, keyPath);
    return TagHelper.mergeTagLists(mergedSources, persisted);
  }, [rawVideoTags, reviewPostId, reviewPost, reduxVideo]);

  const resolvedVideoTags = useMemo(() => {
    if (!videoTagList?.length) return videoTagList;
    const resolveDefinition = (key) => {
      if (!key) return null;
      return tagDictionary[String(key).toLowerCase()] || null;
    };
    return videoTagList.map((tag) => {
      // ... (Keep tag mapping logic) ...
      if (typeof tag === 'string') {
        const definition = resolveDefinition(tag);
        if (definition) return definition;
        if (TagHelper.isLikelyObjectId(tag)) return { _id: tag, tagName: tag };
        return { tagName: tag };
      }
      if (typeof tag === 'object') {
        if (tag.tagName) return tag;
        const definition = resolveDefinition(tag?._id) || resolveDefinition(tag?.name) || resolveDefinition(tag?.tag);
        if (definition) return { ...definition, ...tag };
        if (tag.name && !tag.tagName) return { ...tag, tagName: tag.name };
        return tag;
      }
      return tag;
    });
  }, [videoTagList, tagDictionary]);

  const cachedTagSignature = useMemo(() => getTagSignature(reviewPost?.tags || []), [reviewPost]);
  const computedTagSignature = useMemo(() => getTagSignature(videoTagList), [videoTagList]);
  const videoUrl = typeof window !== 'undefined' ? localStorage.getItem("videoLinkInObjectStore") : null;
  const updatedAt = activeVideo?.updatedAt;
  const videoTitle = videoUrl ? videoUrl.split('/').pop() : "Unknown Title";

  const dataFormatter = (dateString) => {
    if (!dateString || dateString === "Unknown Date") return "Unknown Date";
    const inputDate = new Date(dateString);
    if (isNaN(inputDate.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(inputDate);
  };

  const formattedTitleDate = `${videoTitle} - ${dataFormatter(updatedAt)}`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ... (Keep data fetching effects) ...
  useEffect(() => {
    if (!jwtToken) return;
    let cancelled = false;
    const fetchTags = async () => {
      try {
        const response = await axios.get(GET_ALL_TAGS, { headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' } });
        if (cancelled) return;
        const list = Array.isArray(response?.data?.data) ? response.data.data : [];
        const dictionary = {};
        list.forEach((tag) => {
          if (tag?._id) dictionary[String(tag._id).toLowerCase()] = tag;
          if (tag?.tagName) dictionary[String(tag.tagName).toLowerCase()] = tag;
          if (tag?.name) dictionary[String(tag.name).toLowerCase()] = tag;
        });
        setTagDictionary(dictionary);
      } catch (error) { console.error('Failed to fetch tags', error); }
    };
    fetchTags();
    return () => { cancelled = true; };
  }, [jwtToken]);

  useEffect(() => {
    if (!reviewPostId || typeof window === 'undefined') return;
    if (!reviewPost && reduxVideo) {
      localStorage.setItem(`${reviewPostId}_reviewPost`, JSON.stringify(reduxVideo));
      setReviewPost(reduxVideo);
    }
  }, [reviewPostId, reviewPost, reduxVideo]);

  useEffect(() => {
    if (!reviewPostId || !videoTagList.length || typeof window === 'undefined') return;
    if (cachedTagSignature === computedTagSignature) return;
    const keyPath = reviewPost?.game?.[0]?.path || reduxVideo?.game?.[0]?.path;
    TagHelper.persistTags(reviewPostId, keyPath, videoTagList);
    const snapshot = reviewPost || reduxVideo;
    if (snapshot) {
      const hydrated = { ...snapshot, tags: videoTagList };
      localStorage.setItem(`${reviewPostId}_reviewPost`, JSON.stringify(hydrated));
      setReviewPost(hydrated);
    }
  }, [reviewPostId, videoTagList, cachedTagSignature, computedTagSignature, reviewPost, reduxVideo]);

  useEffect(() => {
    if (!activeVideo || !jwtToken || env === "standalone") return;
    const tnPath = activeVideo.game?.[0]?.tnPath;
    if (!tnPath) return;
    const fetchThumbnail = async () => {
      try {
        const thumbnailResult = await getFetchGamePostFileThumbnail(jwtToken, activeVideo._id, tnPath);
        setVideoThumbnails(prev => ({ ...prev, [reviewPostId]: thumbnailResult }));
      } catch (error) { console.error('Error fetching thumbnail:', error); }
    };
    fetchThumbnail();
  }, [activeVideo, env, jwtToken, reviewPostId]);

  useEffect(() => { try { ReactGA.send({ hitType: 'pageview', page: location.pathname, title: 'Request Feedback' }); } catch { } }, [location.pathname]);

  useEffect(() => {
    if (!reviewPostId) return;
    dispatch(isStarted_GetFetchVideoContent());
    if (videoUrl) {
      dispatch(success_GetFetchVideoContent(videoUrl));
      setVideoLoaded(true);
      try { ReactGA.event({ category: 'GamePost', action: 'video_loaded_request', label: 'url_present' }); } catch { }
    } else {
      dispatch(error_GetFetchVideoContent('No video URL found'));
      try { ReactGA.event({ category: 'GamePost', action: 'video_loaded_request', label: 'url_missing' }); } catch { }
    }
  }, [reviewPostId, dispatch, videoUrl]);


  const changeVideoStatus = async () => {
    try { ReactGA.event({ category: 'GamePost', action: 'submit_video_click' }); } catch { }
    if (env === 'standalone') {
      const nextVideoState = 'review_requested';
      dispatch(updateStandAloneVideoStatusFunction(nextVideoState, reviewPostId));
      navigate('/game-posts/game-videos');
      try { ReactGA.event({ category: 'GamePost', action: 'submit_video_success', label: 'standalone' }); } catch { }
      return;
    }
    const formData = { state: 'REVIEW_REQUESTED' };
    try {
      const response = await changeVideoPostStatus(jwtToken, formData, reviewPostId);
      navigate('/game-posts/game-videos');
      dispatch(toggleVideoSortDropdown());
      setTimeout(() => {
        dispatch(setAlertMessage(response?.data?.message));
        dispatch(setAlertType('success'));
      }, 1000);
      try { ReactGA.event({ category: 'GamePost', action: 'submit_video_success', label: 'api' }); } catch { }
    } catch (error) {
      dispatch(setAlertMessage(error.message));
      dispatch(setAlertType('error'));
      try { ReactGA.event({ category: 'GamePost', action: 'submit_video_error' }); } catch { }
    }
  };

  const cancel = () => {
    navigate('/game-posts/game-videos');
    try { ReactGA.event({ category: 'GamePost', action: 'cancel_request_click' }); } catch { }
  };

  // ─────────────────────────────────────────────────────────────
  // NEW WORLD CLASS DESIGN (TAILWIND)
  // ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50/50 pt-32 pb-12 px-4">
      <div className="max-w-[95%] mx-auto space-y-4"> {/* Widened layout */}

        {/* Header / Breadcrumbs */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
            <span onClick={() => navigate('/game-posts/game-videos')} className="cursor-pointer hover:text-emerald-600 flex items-center gap-1 transition-colors">
              <Video size={16} /> Videos
            </span>
            <ChevronRight size={14} />
            <span className="text-gray-900 font-semibold">Request Feedback</span>
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-gray-800 tracking-tight leading-tight">
            {videoTitle}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 font-medium">
            {dataFormatter(updatedAt)}
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col md:flex-row h-[70vh] min-h-[500px]">

          {/* Left: Video Player Section */}
          <div className="flex-1 bg-black relative flex items-center justify-center">
            {!videoLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            )}

            {videoLoaded && videoContent?.videoContentUrl ? (
              <div className="w-full h-full relative">
                <SimpleVideoPlayer
                  videoUrl={videoContent.videoContentUrl}
                  thumbnailUrl={thumbnailUrl}
                  playerRef={playerRef}
                  videoRef={videoRef}
                  videoKey={videoKey}
                  setLoading={setLoading}
                />
              </div>
            ) : (
              <div className="text-gray-400">Video Content Unavailable</div>
            )}
          </div>

          {/* Right: Details & Actions */}
          <div className="w-full md:w-80 bg-gray-50 border-l border-gray-200 flex flex-col shrink-0">
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Video Details</h3>
              <div className="space-y-4">
                <GameVideoDetails videoTagList={resolvedVideoTags} />
                {resolvedVideoTags?.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No tags detected.</p>
                )}
              </div>


            </div>

            {/* Footer Actions */}
            {videoState === 'created' && (
              <div className="p-6 border-t border-gray-200 bg-white space-y-3">
                <button
                  onClick={changeVideoStatus}
                  className="w-full py-3.5 bg-[#026B33] hover:bg-[#038c43] text-white font-bold rounded-xl shadow-lg shadow-emerald-200 transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <span>Submit for Feedback</span>
                </button>
                <button
                  onClick={cancel}
                  className="w-full py-3 text-gray-500 hover:text-gray-800 font-semibold text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

